import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// Cognito session storage uses localStorage (the SDK default) so the
// signed-in session is shared across browser tabs of this origin —
// opening a link in a new tab keeps the user signed in. Previously this
// service injected a sessionStorage wrapper, which made new tabs land
// on /login because sessionStorage is per-tab.
//
// Security note: tokens persist in localStorage until the user signs
// out or the refresh token expires (~30 days). This is the standard
// pattern for SaaS tools where users routinely work across multiple
// tabs. XSS that can read localStorage can read sessionStorage too —
// the storage choice doesn't materially change the threat model.

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
});

// Keep track of the last authenticated user to avoid getCurrentUser() issues
let lastAuthenticatedUser = null;

// Auth marker — also localStorage so the AuthContext on a freshly-opened
// tab can detect "there was a session here" and restore it.
const AUTH_SESSION_KEY = 'cognito_auth_session_active';

/**
 * Get current authenticated user
 */
export const getCurrentUser = () => {
  // Try the tracked user first, then fall back to userPool
  if (lastAuthenticatedUser) {
    return lastAuthenticatedUser;
  }
  return userPool.getCurrentUser();
};

/**
 * Check if there was an authenticated session (survives HMR/module reload
 * AND new-tab opens, since localStorage is shared across tabs).
 */
export const wasAuthenticated = () => {
  return localStorage.getItem(AUTH_SESSION_KEY) === 'true';
};

/**
 * Mark session as authenticated (called after successful login)
 */
const markSessionAuthenticated = () => {
  localStorage.setItem(AUTH_SESSION_KEY, 'true');
};

/**
 * Clear session authentication marker (called on logout)
 */
const clearSessionMarker = () => {
  localStorage.removeItem(AUTH_SESSION_KEY);
};

/**
 * Get current session (with tokens)
 */
export const getSession = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session.isValid()) {
        reject(new Error('Session is invalid'));
        return;
      }
      resolve(session);
    });
  });
};

/**
 * Token refresh lock to prevent race conditions
 */
let refreshPromise = null;

/**
 * Get valid ID token, refreshing if needed
 * Uses lock to prevent multiple simultaneous refresh calls
 */
export const getIdToken = async () => {
  // If refresh is in progress, wait for it
  if (refreshPromise) {
    const session = await refreshPromise;
    return session.getIdToken().getJwtToken();
  }

  try {
    const session = await getSession();
    const idToken = session.getIdToken();
    const expiresAt = idToken.getExpiration() * 1000;

    // Refresh 5 minutes before expiry
    if (Date.now() > expiresAt - 5 * 60 * 1000) {
      refreshPromise = refreshSession();
      const newSession = await refreshPromise;
      refreshPromise = null;
      return newSession.getIdToken().getJwtToken();
    }

    return idToken.getJwtToken();
  } catch (error) {
    refreshPromise = null;
    throw error;
  }
};

/**
 * Refresh the session using refresh token
 */
export const refreshSession = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      const refreshToken = session.getRefreshToken();
      user.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(newSession);
      });
    });
  });
};

/**
 * Sign in user
 */
export const signIn = (email, password) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        // Store the authenticated user for later retrieval
        lastAuthenticatedUser = user;
        // Mark session as authenticated (survives HMR)
        markSessionAuthenticated();
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes) => {
        // Handle new password required
        reject({ code: 'NewPasswordRequired', userAttributes });
      }
    });
  });
};

/**
 * Sign out user
 */
export const signOut = () => {
  const user = getCurrentUser();
  if (user) {
    user.signOut();
  }
  lastAuthenticatedUser = null;
  // Clear session marker
  clearSessionMarker();
};

/**
 * Get user claims from ID token
 */
export const getUserClaims = async () => {
  const session = await getSession();
  const idToken = session.getIdToken();
  return {
    email: idToken.payload.email,
    userId: idToken.payload.sub,
    tenantId: idToken.payload['custom:tenant_id'],
    role: idToken.payload['custom:role'],
    emailVerified: idToken.payload.email_verified,
  };
};

/**
 * Initiate forgot password flow
 */
export const forgotPassword = (email) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err)
    });
  });
};

/**
 * Confirm new password with verification code
 */
export const confirmPassword = (email, code, newPassword) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err)
    });
  });
};

/**
 * Confirm registration with verification code
 */
export const confirmRegistration = (email, code) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    user.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Resend confirmation code for email verification
 */
export const resendConfirmationCode = (email) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    user.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export { userPool };
